"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QuoteClient = void 0;
const cart_client_1 = require("./cart-client");
/**
 * Extension of the `CartClient` that adds methods specific to working with Quote type Carts.
 * The operations are generally the same except a few additional methods.
 */
class QuoteClient extends cart_client_1.CartClient {
    constructor(options) {
        super({
            ...options,
            basePath: options.basePath || '/api/cart-operations'
        });
    }
    /**
     * Retrieves all submitted quotes that a Seller (admin) should be able to see.
     *
     * @param cq RSQL filter params
     * @param options Additional request configuration including pagination params.
     * @returns A page of quotes
     */
    async retrieveRequestedQuotes(cq, options) {
        const config = {
            method: 'get',
            url: '/quotes',
            params: {
                requested: true,
                cq
            }
        };
        this.attachPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves quotes submitted by a particular buyer (customer).
     *
     * @param cq RSQL filter params
     * @param options Additional request configuration including pagination params.
     * @returns A page of quotes
     */
    async retrieveQuotes(cq, options) {
        const config = {
            method: 'get',
            url: '/quotes',
            params: {
                cq
            }
        };
        this.attachPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Sends a request to update the status of a quote. This includes converting a standard cart
     * to a quote. To do so, set the target status to `DRAFTING`.
     *
     * @param request The request to change a quote's status.
     * @param options Additional request configuration
     * @returns The response to the request including the up-to-date cart.
     */
    async changeQuoteStatus(request, options) {
        if (!request.cartId && !request.cart?.id) {
            throw 'Either cartId or cart must be provided';
        }
        const config = {
            method: 'post',
            url: `/quotes/${request.cartId || request.cart?.id}/status`,
            data: request
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Send a request to delete a quote.
     *
     * @param quoteId Id of the quote to delete
     * @param options Additional request configuration
     */
    async deleteQuote(quoteId, options) {
        const config = {
            method: 'delete',
            url: `/quotes/${quoteId}`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        await this.call(config, options);
    }
    /**
     * Send a request to persist a new `CartNote`.
     *
     * @param cartNote The note to create
     * @param options Additional request configuration
     * @returns The persisted `CartNote`.
     */
    async createNote(cartNote, options) {
        const config = {
            method: 'post',
            url: `/cart-notes/${cartNote.cartId}`,
            data: cartNote
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Send a request to persist a new `CartNote` for an item rather than the cart.
     *
     * @param cartItemId The id of the cart item to add the note to
     * @param cartItemNote The note to create
     * @param options Additional request configuration
     * @returns The persisted `CartNote`.
     */
    async createNoteForItem(cartItemId, cartItemNote, options) {
        cartItemNote.cartItemId = cartItemId;
        const config = {
            method: 'post',
            url: `/cart-notes/${cartItemNote.cartId}/items/${cartItemId}`,
            data: cartItemNote
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves all notes for a cart.
     *
     * @param cartId Id of the parent cart
     * @param cartLevelOnly Whether to only fetch the cart's notes or also the items'.
     * @param cq RSQL filter params
     * @param options Additional request configuration including pagination params.
     * @returns A page of quotes
     */
    async readAllNotesForCart(cartId, cartLevelOnly = false, cq, options) {
        const config = {
            method: 'get',
            url: `/cart-notes/${cartId}`,
            params: {
                cartLevelOnly,
                cq
            }
        };
        this.attachPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Retrieves all notes for a cart item.
     *
     * @param cartId Id of the parent cart.
     * @param cartItemId Id of the parent item.
     * @param cq RSQL filter params
     * @param options Additional request configuration including pagination params.
     * @returns A page of quotes
     */
    async readAllNotesForCartItem(cartId, cartItemId, cq, options) {
        const config = {
            method: 'get',
            url: `/cart-notes/${cartId}/items/${cartItemId}`,
            params: {
                cq
            }
        };
        this.attachPageableParamsIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Send a request to update a `CartNote`.
     *
     * @param cartNote The note to create
     * @param options Additional request configuration
     * @returns The updated `CartNote`.
     */
    async updateNote(note, options) {
        const config = {
            method: 'put',
            url: `/cart-notes/${note.cartId}/notes/${note.id}`,
            data: note
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Send a request to delete a `CartNote`.
     *
     * @param cartNote The note to create
     * @param options Additional request configuration
     * @returns The updated `CartNote`.
     */
    async deleteNote(note, options) {
        const config = {
            method: 'delete',
            url: `/cart-notes/${note.cartId}/notes/${note.id}`
        };
        await this.call(config, options);
    }
    /**
     * Retrieves configuration for a quotes.
     *
     * @param options Additional request configuration.
     * @returns The quote configuration properties
     */
    async readQuoteProperties(options) {
        const config = {
            method: 'get',
            url: `/quotes/properties`
        };
        const response = await this.call(config, options);
        return response?.data;
    }
}
exports.QuoteClient = QuoteClient;
//# sourceMappingURL=quote-client.js.map